import React, { useState, useRef, useEffect } from 'react'
import { SketchPicker, ColorResult } from 'react-color'
import { classNames, Input } from '@cma/common'

interface ColorPickerProps {
  value: string
  onChange: (color: string) => void
  placeholder?: string
  disabled?: boolean
  fullWidth?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const HEX_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/

const PRESET_COLORS = [
  '#D0021B',
  '#F5A623',
  '#F8E71C',
  '#8B572A',
  '#7ED321',
  '#417505',
  '#BD10E0',
  '#9013FE',
  '#4A90E2',
  '#50E3C2',
  '#B8E986',
  '#000000',
  '#4A4A4A',
  '#9B9B9B',
  '#FFFFFF'
]

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  placeholder = '#000000',
  disabled = false,
  fullWidth = false,
  size = 'sm'
}) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false)
  const [inputValue, setInputValue] = useState(value || '')
  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setInputValue(value || '')
  }, [value])

  const handleSwatchClick = () => {
    if (!disabled) setDisplayColorPicker((v) => !v)
  }

  const handleClose = () => setDisplayColorPicker(false)

  const handleColorChange = (color: ColorResult) => {
    const hexColor = color.hex
    setInputValue(hexColor)
    onChange(hexColor)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setInputValue(newValue)

    const cleanValue = newValue.trim()
    if (!cleanValue) {
      onChange('')
      return
    }

    let hexValue = cleanValue
    if (!hexValue.startsWith('#')) hexValue = '#' + hexValue
    if (HEX_REGEX.test(hexValue)) onChange(hexValue)
  }

  const handleInputBlur = () => {
    let cleanValue = inputValue.trim()
    if (!cleanValue.startsWith('#')) cleanValue = '#' + cleanValue
    if (HEX_REGEX.test(cleanValue) && cleanValue !== inputValue) {
      setInputValue(cleanValue)
      onChange(cleanValue)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleInputBlur()
  }

  const getDisplayColor = () => {
    let colorValue = inputValue.trim()
    if (!colorValue.startsWith('#')) colorValue = '#' + colorValue
    return HEX_REGEX.test(colorValue) ? colorValue : '#ffffff'
  }

  return (
    <div className="relative">
      <div className="relative">
        <Input
          fullWidth={fullWidth}
          size={size}
          placeholder={placeholder}
          disabled={disabled}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          style={{ paddingLeft: '2.2rem' }}
        />
        <button
          type="button"
          disabled={disabled}
          aria-expanded={displayColorPicker}
          aria-label="Open color picker"
          aria-controls="color-picker-popover"
          title="Click to open color picker"
          onClick={handleSwatchClick}
          className={classNames(
            'absolute left-2 top-1/2 -translate-y-1/2 rounded border border-gray-300 p-0',
            {
              'cursor-not-allowed opacity-50': disabled,
              'cursor-pointer hover:border-gray-400': !disabled
            }
          )}>
          <div
            className="h-5 w-5 rounded border border-gray-200"
            style={{ backgroundColor: getDisplayColor() }}
          />
        </button>
      </div>

      {displayColorPicker && (
        <div ref={pickerRef} className="absolute left-0 top-full z-50 mt-2">
          <div
            className="fixed inset-0"
            onClick={handleClose}
            onKeyDown={(e) => e.key === 'Escape' && handleClose()}
            role="button"
            tabIndex={0}
            aria-label="Close color picker"
          />
          <div className="relative" id="color-picker-popover">
            <SketchPicker
              color={getDisplayColor()}
              onChange={handleColorChange}
              presetColors={PRESET_COLORS}
              disableAlpha
            />
          </div>
        </div>
      )}
    </div>
  )
}
